/**
 * Reports List - Display and manage all reports
 */

import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card } from '../UI/Card';
import { Button } from '../UI/Button';
import { Input } from '../UI/Input';
import { Table, Column } from '../UI/Table';
import { Badge } from '../UI/Badge';
import { LoadingSpinner } from '../UI/LoadingSpinner';
import { listReports, downloadReportPDF, downloadReportV1, downloadReportV2, trackReportDownload, listReportJobs } from '../../api/reports';
import { useWorkspace } from '../../hooks/useWorkspace';
import { Report, ReportJobStatus } from '../../types/api';
import {
  FileText,
  Download,
  Eye,
  Calendar,
  Filter,
  Search,
  FileCheck,
  Clock,
  TrendingUp,
  AlertCircle,
  CheckCircle,
  Activity,
} from 'lucide-react';

interface ReportsListProps {
  onViewReport?: (reportId: string) => void;
  onCreateNew?: () => void;
  viewMode?: 'cards' | 'table';
  queryKeySuffix?: string; // Optional suffix for query key (e.g., 'rfp_proposals')
  listReportsOptions?: Parameters<typeof listReports>[1]; // Options to pass to listReports
}

export function ReportsList({ 
  onViewReport, 
  onCreateNew, 
  viewMode = 'cards',
  queryKeySuffix,
  listReportsOptions,
}: ReportsListProps) {
  const { projectId } = useWorkspace();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');

  // Build query key with optional suffix
  const queryKey = queryKeySuffix 
    ? ['reports', projectId, queryKeySuffix]
    : ['reports', projectId];

  const { data: reports, isLoading, refetch } = useQuery({
    queryKey,
    queryFn: () => listReports(projectId!, listReportsOptions),
    enabled: !!projectId,
    refetchInterval: 30000, // Refresh every 30s
  });

  // Fetch pending report generation jobs
  const { data: reportJobs, isLoading: jobsLoading } = useQuery({
    queryKey: ['report-jobs', projectId],
    queryFn: () => listReportJobs(projectId!),
    enabled: !!projectId,
    refetchInterval: 5000, // Refresh every 5s for active jobs
  });

  // Combine reports and pending jobs
  const allItems = React.useMemo(() => {
    const reportItems = (reports || []).map(r => ({ type: 'report' as const, data: r }));
    const jobItems = (reportJobs || []).map(j => ({ type: 'job' as const, data: j }));
    const combined = [...jobItems, ...reportItems]; // Show pending jobs first
    console.log(`[ReportsList] Combined items: ${jobItems.length} jobs, ${reportItems.length} reports, total: ${combined.length}`);
    return combined;
  }, [reports, reportJobs]);

  const filteredReports = React.useMemo(() => {
    if (!allItems) return [];
    
    return allItems.filter((item) => {
      const name = item.type === 'report' 
        ? (item.data.name || item.data.client_name)
        : (item.data as ReportJobStatus).input_data?.name || (item.data as ReportJobStatus).input_data?.client_name || 'Pending Report';
      const type = item.type === 'report'
        ? item.data.report_type
        : (item.data as ReportJobStatus).input_data?.report_type || 
          (item.data as ReportJobStatus).input_data?.metadata?.frontend_type || 
          'compliance';
      
      const matchesSearch = 
        name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        type?.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesType = filterType === 'all' || type === filterType;
      const matchesStatus = filterStatus === 'all' || 
        (item.type === 'report' ? item.data.status === filterStatus :
         (item.data as ReportJobStatus).status === filterStatus);
      
      return matchesSearch && matchesType && matchesStatus;
    });
  }, [allItems, searchTerm, filterType, filterStatus]);

  const handleDownload = async (reportId: string, format: 'v1' | 'v2' | 'pdf') => {
    try {
      // Track download
      trackReportDownload(reportId, format).catch(err => console.error('Track failed:', err));

      if (format === 'pdf') {
        const blob = await downloadReportPDF(reportId);
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `report_${reportId}.pdf`;
        a.click();
        window.URL.revokeObjectURL(url);
      } else if (format === 'docx') {
        const { downloadReportDOCX } = await import('../../api/reports');
        const blob = await downloadReportDOCX(reportId);
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `report_${reportId}.docx`;
        a.click();
        window.URL.revokeObjectURL(url);
      } else {
        const content = format === 'v1' 
          ? await downloadReportV1(reportId)
          : await downloadReportV2(reportId, false); // view=false for actual download
        const blob = new Blob([content], { type: 'text/markdown' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `report_${reportId}_${format}.md`;
        a.click();
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Download failed:', error);
    }
  };

  // Determine actual report status based on evidence gaps
  const getReportStatus = (report: Report) => {
    if (report.status === 'archived') return 'archived';
    if (report.status === 'submitted') return 'submitted';
    if (report.status === 'approved') return 'approved';
    
    // If has evidence gaps, it's not truly "completed"
    if ((report.evidence_gaps || 0) > 0) {
      return 'needs_evidence';
    }
    
    // If questions not all answered
    if ((report.answered_questions || 0) < (report.total_questions || 1)) {
      return 'in_progress';
    }
    
    // Status from backend or default to completed if all questions answered and no gaps
    return report.status || 'completed';
  };

  const reportTypeColors = {
    compliance: 'from-blue-500 to-blue-600',
    architecture: 'from-purple-500 to-purple-600',
    risk: 'from-red-500 to-red-600',
    workflow: 'from-green-500 to-green-600',
    custom: 'from-gray-500 to-gray-600',
    rfp_proposal: 'from-indigo-500 to-indigo-600',
    // MineScope India
    monthly_ec: 'from-emerald-500 to-emerald-600',
    six_monthly_ec: 'from-teal-500 to-teal-600',
    water_quality: 'from-cyan-500 to-cyan-600',
    form_m: 'from-amber-500 to-amber-600',
    cto_quarterly: 'from-orange-500 to-orange-600',
    exceedance_alert: 'from-red-500 to-red-600',
    caaqms_monthly: 'from-indigo-500 to-indigo-600',
    annual_environmental: 'from-purple-500 to-purple-600',
    fc_compliance: 'from-green-500 to-green-600',
    // PFAS/MARPAC/Keystone
    quarterly_monitoring: 'from-blue-500 to-blue-600',
    annual_report: 'from-purple-500 to-purple-600',
    environmental_impact: 'from-green-500 to-green-600',
    client_deliverable: 'from-indigo-500 to-indigo-600',
  };

  const reportTypeIcons = {
    compliance: FileCheck,
    architecture: FileText,
    risk: AlertCircle,
    workflow: TrendingUp,
    custom: FileText,
    rfp_proposal: FileText,
    monthly_ec: Calendar,
    six_monthly_ec: Calendar,
    water_quality: FileCheck,
    form_m: FileText,
    cto_quarterly: Clock,
    exceedance_alert: AlertCircle,
    caaqms_monthly: Activity,
    annual_environmental: Calendar,
    fc_compliance: CheckCircle,
    quarterly_monitoring: Calendar,
    annual_report: Calendar,
    environmental_impact: TrendingUp,
    client_deliverable: FileText,
  };

  if (!projectId) {
    return (
      <Card>
        <p className="text-center py-12 text-gray-500 dark:text-gray-300">Select a project to view reports</p>
      </Card>
    );
  }

  if (isLoading) {
    return <LoadingSpinner message="Loading reports..." />;
  }

  return (
    <div className="space-y-6">
      {/* Search and Filters */}
      <Card>
        <div className="space-y-4">
          <div className="flex gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 dark:text-gray-300" />
              <Input
                placeholder="Search reports by name or type..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Button onClick={onCreateNew} className="whitespace-nowrap">
              <FileText className="h-4 w-4" />
              New Report
            </Button>
          </div>

          <div className="flex gap-4">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                <Filter className="inline h-4 w-4 mr-1" />
                Filter by Type
              </label>
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">All Types</option>
                {/* Dynamic options from reports and jobs data */}
                {Array.from(new Set([
                  ...(reports?.map(r => r.report_type).filter(Boolean) || []),
                  ...(reportJobs?.map(j => j.input_data?.report_type || j.input_data?.metadata?.frontend_type).filter(Boolean) || [])
                ])).map(type => (
                  <option key={type} value={type}>
                    {type?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Filter by Status
              </label>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">All Statuses</option>
                <option value="queued">Queued</option>
                <option value="running">Running</option>
                <option value="pending">Pending</option>
                <option value="checkpointed">Checkpointed</option>
                <option value="draft">Draft</option>
                <option value="in_review">In Review</option>
                <option value="approved">Approved</option>
                <option value="submitted">Submitted</option>
                <option value="archived">Archived</option>
              </select>
            </div>
          </div>
        </div>
      </Card>

      {/* Reports Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredReports.length === 0 ? (
          <div className="col-span-full">
            <Card>
              <div className="text-center py-12">
                <FileText className="h-12 w-12 text-gray-400 dark:text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 dark:text-gray-300">No reports found</p>
                <Button onClick={onCreateNew} className="mt-4">
                  Create Your First Report
                </Button>
              </div>
            </Card>
          </div>
        ) : (
          filteredReports.map((item) => {
            if (item.type === 'job') {
              const job = item.data as ReportJobStatus;
              const displayName = job.input_data?.name || job.input_data?.client_name || 'Pending Report';
              const reportType = job.input_data?.metadata?.frontend_type || job.input_data?.report_type || 'compliance';
              const templateName = job.input_data?.metadata?.template_name || reportType.replace(/_/g, ' ');
              const progress = job.progress_data || {};
              // When job is completed, ensure we show final progress (100%)
              // Check both job-level and progress_data for final values
              let questionsComplete = job.questions_complete || progress.questions_complete || 0;
              let totalQuestions = job.total_questions || progress.total_questions || 0;
              
              // If job is completed but progress shows incomplete, use total_questions as complete
              if (job.status === 'completed' && questionsComplete < totalQuestions && totalQuestions > 0) {
                questionsComplete = totalQuestions; // Ensure 100% when completed
              }
              
              const progressPercent = totalQuestions > 0 
                ? Math.min(100, Math.round((questionsComplete / totalQuestions) * 100)) 
                : (job.status === 'completed' ? 100 : 0);
              
              const IconComponent = reportTypeIcons[reportType as keyof typeof reportTypeIcons] || FileText;
              const gradientColor = reportTypeColors[reportType as keyof typeof reportTypeColors] || reportTypeColors.custom;
              
              return (
                <Card key={job.id} className="hover:shadow-lg transition-shadow border-2 border-blue-200 dark:border-blue-800">
                  <div className="space-y-4">
                    {/* Header */}
                    <div className="flex items-start gap-3">
                      <div className={`p-3 bg-gradient-to-br ${gradientColor} rounded-lg shadow-md`}>
                        <IconComponent className="h-6 w-6 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 truncate">
                          {displayName}
                        </h3>
                        <p className="text-sm text-gray-500 dark:text-gray-300 capitalize">
                          {templateName}
                        </p>
                      </div>
                    </div>

                    {/* Progress Bar */}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-gray-600 dark:text-gray-400">Progress</span>
                        <span className="font-medium text-gray-900 dark:text-gray-100">{progressPercent}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${progressPercent}%` }}
                        />
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-300">
                        {questionsComplete} of {totalQuestions} questions complete
                        {job.current_question && (
                          <div className="mt-1 italic">Current: {job.current_question.substring(0, 50)}...</div>
                        )}
                      </div>
                    </div>

                    {/* Stats */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-gray-50 dark:bg-gray-800 p-3 rounded-lg">
                        <div className="text-xs text-gray-500 dark:text-gray-300 mb-1">Status</div>
                        <div className="text-lg font-bold text-gray-900 dark:text-gray-100 capitalize">
                          {job.status}
                        </div>
                      </div>
                      <div className="bg-gray-50 dark:bg-gray-800 p-3 rounded-lg">
                        <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Cache Hits</div>
                        <div className="text-lg font-bold text-gray-900 dark:text-gray-100">
                          {job.cache_hits || 0}
                        </div>
                      </div>
                    </div>

                    {/* Status Badge */}
                    <div className="flex flex-wrap gap-2">
                      <Badge 
                        variant={
                          job.status === 'running' ? 'info' :
                          job.status === 'queued' ? 'default' :
                          job.status === 'pending' ? 'default' :
                          'warning'
                        }
                      >
                        {job.status === 'running' && <Activity className="h-3 w-3 mr-1 animate-pulse" />}
                        {job.status === 'queued' && <Clock className="h-3 w-3 mr-1" />}
                        {job.status.charAt(0).toUpperCase() + job.status.slice(1)}
                      </Badge>
                      {job.eta_seconds && (
                        <Badge variant="default">
                          ETA: {Math.round(job.eta_seconds / 60)}m
                        </Badge>
                      )}
                    </div>

                    {/* Metadata */}
                    <div className="text-xs text-gray-500 dark:text-gray-300 space-y-1">
                      <div className="flex items-center gap-2">
                        <Calendar className="h-3 w-3" />
                        Started: {job.started_at ? new Date(job.started_at).toLocaleString() : new Date(job.created_at).toLocaleString()}
                      </div>
                      {job.current_section && (
                        <div className="flex items-center gap-2">
                          <FileText className="h-3 w-3" />
                          Section: {job.current_section}
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2 pt-2 border-t border-gray-200 dark:border-gray-700">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled
                        className="flex-1"
                      >
                        <Clock className="h-4 w-4" />
                        Generating...
                      </Button>
                    </div>
                  </div>
                </Card>
              );
            } else {
              const report = item.data as Report;
              const reportType = report.report_type || 'custom';
              const IconComponent = reportTypeIcons[reportType as keyof typeof reportTypeIcons] || FileText;
              const gradientColor = reportTypeColors[reportType as keyof typeof reportTypeColors] || reportTypeColors.custom;
              const displayName = report.name || report.client_name || 'Untitled Report';
              
              return (
                <Card key={report.id} className="hover:shadow-lg transition-shadow">
                  <div className="space-y-4">
                    {/* Header */}
                    <div className="flex items-start gap-3">
                      <div className={`p-3 bg-gradient-to-br ${gradientColor} rounded-lg shadow-md`}>
                        <IconComponent className="h-6 w-6 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 truncate">
                          {displayName}
                        </h3>
                        <p className="text-sm text-gray-500 dark:text-gray-300 capitalize">
                          {report.report_type?.replace(/_/g, ' ')}
                        </p>
                      </div>
                    </div>

                    {/* Stats */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-gray-50 dark:bg-gray-800 p-3 rounded-lg">
                        <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Questions</div>
                        <div className="text-lg font-bold text-gray-900 dark:text-gray-100">
                          {report.answered_questions || 0}/{report.total_questions || 0}
                        </div>
                      </div>
                      <div className="bg-gray-50 dark:bg-gray-800 p-3 rounded-lg">
                        <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Confidence</div>
                        <div className="text-lg font-bold text-gray-900 dark:text-gray-100">
                          {report.avg_confidence ? `${(report.avg_confidence * 100).toFixed(0)}%` : '-'}
                        </div>
                      </div>
                      <div className="bg-gray-50 dark:bg-gray-800 p-3 rounded-lg">
                        <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Citations</div>
                        <div className="text-lg font-bold text-gray-900 dark:text-gray-100">
                          {report.total_citations || 0}
                        </div>
                      </div>
                      <div className="bg-gray-50 dark:bg-gray-800 p-3 rounded-lg">
                        <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Gaps</div>
                        <div className="text-lg font-bold text-gray-900 dark:text-gray-100">
                          {report.evidence_gaps || 0}
                        </div>
                      </div>
                    </div>

                    {/* Status Badge */}
                    <div className="flex flex-wrap gap-2">
                      {(() => {
                        const actualStatus = getReportStatus(report);
                        return (
                          <Badge 
                            variant={
                              actualStatus === 'approved' ? 'success' :
                              actualStatus === 'submitted' ? 'success' :
                              actualStatus === 'completed' ? 'success' :
                              actualStatus === 'in_review' ? 'info' :
                              actualStatus === 'needs_evidence' ? 'warning' :
                              actualStatus === 'in_progress' ? 'warning' :
                              'default'
                            }
                          >
                            {actualStatus === 'needs_evidence' ? `${report.evidence_gaps} Evidence Gap${report.evidence_gaps !== 1 ? 's' : ''}` : 
                             actualStatus.replace(/_/g, ' ')}
                          </Badge>
                        );
                      })()}
                      {report.avg_confidence && report.avg_confidence < 0.7 && (
                        <Badge variant="warning">Low Confidence</Badge>
                      )}
                    </div>

                    {/* Metadata */}
                    <div className="text-xs text-gray-500 dark:text-gray-300 space-y-1">
                      <div className="flex items-center gap-2">
                        <Calendar className="h-3 w-3" />
                        Created: {new Date(report.created_at).toLocaleDateString()}
                      </div>
                      {report.updated_at && (
                        <div className="flex items-center gap-2">
                          <Clock className="h-3 w-3" />
                          Updated: {new Date(report.updated_at).toLocaleDateString()}
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2 pt-2 border-t border-gray-200 dark:border-gray-700">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onViewReport?.(report.id)}
                        className="flex-1"
                      >
                        <Eye className="h-4 w-4" />
                        View
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDownload(report.id, 'pdf')}
                        title="Download PDF"
                      >
                        <Download className="h-4 w-4" />
                        PDF
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDownload(report.id, 'docx')}
                        title="Download DOCX"
                      >
                        <Download className="h-4 w-4" />
                        DOCX
                      </Button>
                    </div>
                  </div>
                </Card>
              );
            }
          })
        )}
      </div>

      {/* Summary Stats */}
      {filteredReports.length > 0 && (
        <Card>
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-6 flex-wrap">
              <div className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {filteredReports.length} Total
                </span>
              </div>
              {filteredReports.filter(item => item.type === 'job').length > 0 && (
                <div className="flex items-center gap-2">
                  <Activity className="h-5 w-5 text-blue-500 dark:text-blue-400" />
                  <span className="text-sm text-gray-600 dark:text-gray-300">
                    {filteredReports.filter(item => item.type === 'job').length} Generating
                  </span>
                </div>
              )}
              <div className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-green-500 dark:text-green-400" />
                <span className="text-sm text-gray-600 dark:text-gray-300">
                  {filteredReports.filter(item => 
                    item.type === 'report' && (item.data as Report).status === 'approved'
                  ).length} Approved
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="h-5 w-5 text-yellow-500 dark:text-yellow-400" />
                <span className="text-sm text-gray-600 dark:text-gray-300">
                  {filteredReports.filter(item => 
                    item.type === 'report' && (item.data as Report).status === 'in_review'
                  ).length} In Review
                </span>
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={() => refetch()}>
              Refresh
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
}
